import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { Router, Switch, Route, NavLink, useRouteMatch, useHistory, Link, Redirect } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import Icon from 'components/Icon'
import Supplier from './components/Supplier'
import Chat from './components/Chat'
import Invite from './components/Invite'
import AsyncSelect from 'react-select/async'
import Select from 'react-select'
import debounce from 'debounce-promise'
import Activity from 'containers/SupplierActivity'
import { statuses } from './constants'
import { Full as Loading } from 'components/Loading'
import { Button, Checkbox } from '@arnsbogroup/react-voicearchive'
import useApi from 'hooks/useApi'

const AdminSupplierOnboarding = ({ match, location }) => {
  const { formatMessage } = useIntl()

  const [filterBy, setFilterBy] = useState(null)
  const [filterOrigin, setFilterOrigin] = useState(null)
  const [filterLanguages, setFilterLanguages] = useState([])
  const [filterGender, setFilterGender] = useState(null)
  const [filterIsSuspicious, setFilterIsSuspicious] = useState(false)
  const [expandedSupplier, setExpandedSupplier] = useState(null)
  const [showInvitePane, setShowInvitePane] = useState(false)
  const [pane, setPane] = useState('ACTIVITY')
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(false)
  const [languageGroups] = useApi(`/rest/v2/language/groups`)

  useEffect(() => {
    if (!!filterLanguages.length || filterIsSuspicious) {
      onFetchSuppliers();
    }
    if (!filterLanguages.length) {
      setSuppliers([]);
    }
  }, [showInvitePane, filterLanguages, filterIsSuspicious])

  const onFetchSuppliers = () => {
    setLoading(true)
    let params = new URLSearchParams()

    params.append('isSuspicious', filterIsSuspicious)
    filterLanguages.map(({ value }) => {
      params.append('languageId[]', value)
    })

    fetch(`/supplier/onboarding.json?` + params.toString())
      .then((response) => response.json())
      .then(({ data }) => setSuppliers(data))
      .then(() => setLoading(false))
  }

  const onSupplierUpdate = ({ uid }) => ({ status, supplier }) => {
    if (status !== undefined) {
      fetch(`/supplier/setStatus.json?supplierId=${uid}&statusId=${status}`, { method: 'POST' })
        .then((response) => response.json())
        .then((nextSupplier) => setSuppliers(suppliers.map((prevSupplier) => (
          prevSupplier.uid == uid ? { ...prevSupplier, status } : prevSupplier
        ))))
        .catch((error) => window.alert(error))
    }

    if (supplier !== undefined) {
      fetch(`/rest/v2/supplier/${uid}`, { method: 'PUT', body: JSON.stringify(supplier) })
        .then((response) => response.json())
        .then((nextSupplier) => setSuppliers(suppliers.map((prevSupplier) => (
          prevSupplier.uid == nextSupplier.uid ? { ...prevSupplier, ...supplier, ...nextSupplier } : prevSupplier
        ))))
        .catch((error) => window.alert(error))
    }
  }
  
  const onSuspiciousChange = (supplier) => {
    fetch(`/rest/v2/supplier/${supplier.uid}`, { method: 'PUT', body: JSON.stringify({isSuspicious: !Boolean(supplier.isSuspicious)}) })
      .then((response) => response.json())
      .then(() => onFetchSuppliers())
      .catch((error) => window.alert(error))
  }

  const totalCounts = useMemo(() => {
    return statuses.reduce((memo, [id, name]) => {
      memo[id] = suppliers.filter(({ status }) => status == id).length
      return memo
    }, {})
  }, [suppliers])

  const filtered = useMemo(() => {
    return suppliers.filter(({ status, languages, referer, gender }) => {
      if (
        filterLanguages &&
        filterLanguages.length &&
        !languages.some(({ uid }) => filterLanguages.map(({ value }) => parseInt(value)).includes(parseInt(uid)))
      ) {
        return false
      }

      if (filterBy !== null && filterBy !== undefined && parseInt(filterBy, 10) !== parseInt(status, 10)) {
        return false
      }

      if (filterOrigin && filterOrigin == 'Existing' && referer !== 'EXISTING') {
        return false
      }

      if (filterOrigin && filterOrigin == 'New' && referer === 'EXISTING') {
        return false
      }

      if (filterGender !== null && filterGender !== parseInt(gender, 10)) {
        return false
      }

      return true
    })
  }, [suppliers, filterBy, filterLanguages, filterOrigin, filterGender])

  const statusOptions = [[null, 'All statuses'], ...statuses].map(([id, name]) =>
    ({ value: id, label: totalCounts[id] ? `${name} (${totalCounts[id]})` : name })
  )

  const originOptions = [
    { value: null, label: 'All origins' }, 
    { value: 'Existing', label: 'Existing' }, 
    { value: 'New', label: 'New' }, 
  ]

  const genderOptions = [
    { value: null, label: 'All genders' }, 
    { value: 0, label: 'Non binaries' }, 
    { value: 1, label: 'Males' }, 
    { value: 2, label: 'Females' }, 
  ]

  const onFilterLanguagesChange = (languages) => {
    if (!languages || typeof languages[0] === 'undefined') {
      setFilterLanguages([])
    } else if (languageGroups.find(languageGroup => languageGroup.uid === languages[0].value && languages[0]?.isLanguageGroup)) {
      const group = languageGroups.find(languageGroup => languageGroup.uid === languages[0].value);
      setFilterLanguages(group.languages.map(language => ({
        label: language.name,
        value: language.uid
      })))
    } else {
      setFilterLanguages(languages)
    }
  }

  return (
    <div className="flex flex-col w-full h-full">
      <div className="w-full flex items-center justify-between border-b border-gray-400 bg-gray-100 p-4">
        <div className="w-full flex justify-start space-x-4">
          <div className="w-2/6">
            <AsyncSelect
              value={filterLanguages}
              onChange={onFilterLanguagesChange}
              isMulti={true}
              placeholder="Filter by language"
              defaultOptions={languageGroups && !!languageGroups.length ? languageGroups.map((languageGroup) => ({label: languageGroup.name, value: languageGroup.uid, isLanguageGroup: true})) : []}
              loadOptions={
                debounce((query) => fetch(`/rest/v2/language?search[name]=${query}`)
                  .then((response) => response.json())
                  .then((languages) => languages.map(({ uid, name }) => ({ value: uid, label: name })))
                , 250)
              }
            />
          </div>

          <div className="w-1/6">
            <Select
              value={statusOptions.find(({ value }) => value == filterBy)}
              onChange={({ value }) => setFilterBy(value)}
              placeholder="Status"
              options={statusOptions}
            />
          </div>

          <div className="w-1/6">
            <Select
              value={originOptions.find(({ value }) => value == filterOrigin)}
              onChange={({ value }) => setFilterOrigin(value)}
              placeholder="Origin"
              options={originOptions}
            />
          </div>

          <div className="w-1/6">
            <Select
              value={genderOptions.find(({ value }) => value == filterGender)}
              onChange={({ value }) => setFilterGender(value)}
              placeholder="Gender"
              options={genderOptions}
            />
          </div>
          
          <div className="w-1/6">
              <Checkbox
                onChange={() => {setFilterIsSuspicious(!filterIsSuspicious)}}
                checked={filterIsSuspicious}
                label="Suspicious applicants" 
              />
          </div>
        </div>

        <Button
          onClick={() => setShowInvitePane(true)}
        >
            Invite
        </Button>

      </div>

      <div className="grow overflow-hidden flex w-full h-full">
        <div className={`${expandedSupplier ? 'w-3/4' : 'w-full'} h-full overflow-y-scroll`}>
          { loading ? (
            <Loading />
          ) : !filtered.length ? (
            <div className="text-gray-600 w-full text-center py-10">Select one or more languages, or <button className="text-primary cursor-pointer" onClick={onFetchSuppliers}>click here</button> to load all.</div>
          ) : filtered.map((supplier) => (
            <Supplier
              key={supplier.uid}
              supplier={supplier}
              expanded={expandedSupplier ? supplier.uid == expandedSupplier.uid : false}
              onExpand={() => setExpandedSupplier(supplier)}
              onCollapse={() => setExpandedSupplier(null)}
              onChange={onSupplierUpdate(supplier)}
              refetch={() => onFetchSuppliers()}
              onSuspiciousChange={() => onSuspiciousChange(supplier)}
            />
          )) }
        </div>

        { showInvitePane &&
          <div className="w-1/4 h-full flex flex-col border-l border-gray-400">
            <Invite onSubmit={() => setShowInvitePane(false)} />
          </div>
        }

        { expandedSupplier &&
          <div className="w-1/4 h-full flex flex-col border-l border-gray-400">
            <div className="flex items-center justify-around p-2 text-primary border-b border-gray-400">
              <Button 
                onClick={() => setPane('ACTIVITY')}
                variant={pane == 'ACTIVITY' ? 'solid' : 'outline'}
              >
                Activity
              </Button>
              <Button 
                onClick={() => setPane('CHAT')} 
                variant={pane == 'CHAT' ? 'solid' : 'outline'}
              >
                  Messages
              </Button>
            </div>

            <div className="grow overflow-hidden">
              { pane == 'CHAT' &&
                <Chat supplier={expandedSupplier} />
              }
              { pane == 'ACTIVITY' &&
                <Activity supplier={expandedSupplier} />
              }
            </div>
          </div>
        }
      </div>
    </div>
  )
}

export default AdminSupplierOnboarding
